import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  startAt,
  endAt,
  limit,
  getDocs
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

import BackHomeButton from "../components/BackHomeButton";

const Email_Quiz = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract Quiz Data from navigation state
  const { Quiz_Text, subject, topic, format, standard, lesson, school } = location.state || {};

  const [friendName, setFriendName] = useState("");
  const [friendEmail, setFriendEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const functions = getFunctions();


  const MAX_PER_MINUTE = 1;
  const WINDOW_TIME = 60 * 1000; // 1 minute

  const [email, setEmail] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced Firestore Query for User lookup
  const handleChange = (e) => {
    const value = e.target.value.toLowerCase();
    setEmail(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length < 2) {
      setSuggestions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const q = query(
          collection(db, "profiles"),
          orderBy("email"),
          startAt(value),
          endAt(value + "\uf8ff"),
          limit(10)
        );

        const querySnapshot = await getDocs(q);
        const results = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setSuggestions(results);
      } catch (error) {
        console.error("Error fetching emails:", error);
      } finally {
        setLoading(false);
      }
    }, 400);
  };

  const handleSelect = (selectedEmail) => {
    setEmail(selectedEmail);
    setFriendEmail(selectedEmail);
    setSuggestions([]);
  };

  function canSendEmail() {
    const now = Date.now();
    const stored = JSON.parse(localStorage.getItem("emailRate_Quiz")) || {
      count: 0,
      startTime: now,
    };

    if (now - stored.startTime > WINDOW_TIME) {
      localStorage.setItem(
        "emailRate_Quiz",
        JSON.stringify({ count: 1, startTime: now })
      );
      return true;
    }

    if (stored.count >= MAX_PER_MINUTE) {
      return false;
    }

    stored.count += 1;
    localStorage.setItem("emailRate_Quiz", JSON.stringify(stored));
    return true;
  }

// 1. Point to your existing backend function
  const sendEmail = httpsCallable(functions, "sendWorksheetEmail");

  const handleShare = async (e) => {
    e.preventDefault();

    if (!friendName.trim() || !friendEmail.trim()) {
      alert("Please fill required fields");
      return;
    }

    if (!canSendEmail()) {
      alert("Please wait before sending more emails.");
      return;
    }

    if (!Quiz_Text || Quiz_Text === 'No_Content') {
      alert("Quiz data is empty or invalid.");
      return;
    }

    try {
      setLoading(true);

      // 2. Format the text for HTML! 
      // The backend renders message inside <p> tags, so \n will be ignored. We must use <br />
      const htmlFormattedQuiz = Quiz_Text.replace(/\n/g, "<br />");
      
      // Combine the user's custom message with the formatted quiz
      const combinedMessage = message.trim() 
        ? `${message}<br /><br /><strong>--- AI GENERATED QUIZ ---</strong><br /><br />${htmlFormattedQuiz}`
        : `<strong>--- AI GENERATED QUIZ ---</strong><br /><br />${htmlFormattedQuiz}`;

      // 3. Send the payload matching the exact keys the backend expects
      const result = await sendEmail({
        friendName: friendName,
        friendEmail: friendEmail,
        worksheetTitle: `AI Quiz: ${subject || 'Mixed'} - ${topic || 'General'}`,
        fileUrl: "N/A", // Backend expects this key, even though it doesn't render it
        message: combinedMessage, 
      });

      //console.log("Email result:", result.data);

      // 4. Save Share Record to Firestore
      await addDoc(collection(db, "Shared_Quizzes"), {
        Quiz_Text,
        subject: subject || "Unknown",
        topic: topic || "Unknown",
        format: format || "Unknown",
        standard: standard || "Unknown",
        lesson: lesson || "Unknown",
        school: school || "Unknown",
        sharedBy: auth.currentUser?.uid || "Anonymous",
        sharedByEmail: auth.currentUser?.email || "Unknown",
        friendName,
        friendEmail,
        message,
        createdAt: serverTimestamp(),
      });

      setSuccess(true);
      setFriendName("");
      setFriendEmail("");
      setEmail("");
      setMessage("");
    } catch (error) {
      console.error("Share error:", error);
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!Quiz_Text) {
    return (
      <div className="min-h-screen flex items-center justify-center font-sans text-slate-500">
        No quiz data found to share.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center px-4 font-sans">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-8">
        <div className="mb-4">
          <BackHomeButton />
        </div>

        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Share Quiz</h2>
          <p className="text-sm text-gray-500 mt-1">
            Email this generated quiz to a friend or student
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleShare} className="space-y-4">
          <input
            type="text"
            placeholder="Recipient's Name"
            value={friendName}
            onChange={(e) => setFriendName(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all"
          />

          <div ref={wrapperRef} className="relative w-full">
            <input
              type="email"
              value={email}
              onChange={handleChange}
              placeholder="Recipient's Email"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all"
            />

            {loading && (
              <div className="absolute right-3 top-3.5 text-xs text-indigo-400 font-medium animate-pulse">
                Searching...
              </div>
            )}

            {suggestions.length > 0 && (
              <div className="absolute z-20 w-full bg-white border border-gray-200 rounded-lg mt-1 shadow-lg max-h-60 overflow-y-auto">
                {suggestions.map((profile) => (
                  <div
                    key={profile.id}
                    onClick={() => handleSelect(profile.email)}
                    className="p-3 hover:bg-indigo-50 cursor-pointer border-b border-gray-100 last:border-0"
                  >
                    <div className="font-semibold text-gray-800">{profile.email}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {profile.name || "No Name"} • {profile.school || "No School"} • Std: {profile.standard || "N/A"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <textarea
            placeholder="Optional message (e.g. Try this quiz!)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows="3"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none transition-all resize-y"
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3.5 rounded-xl font-bold text-sm transition-all shadow-md hover:shadow-lg disabled:opacity-50"
          >
            {loading ? "Sending..." : "📧 Send Quiz"}
          </button>
        </form>

        {/* Quiz Details Preview */}
        <div className="mt-8 border border-indigo-100 rounded-xl p-5 bg-indigo-50/50">
          <h3 className="text-sm font-bold text-indigo-900 mb-3 flex items-center gap-2">
            <span>📋</span> Quiz Details
          </h3>
          <div className="space-y-1.5">
            <p className="text-sm font-semibold text-gray-800">Subject: <span className="font-normal text-gray-600">{subject}</span></p>
            <p className="text-sm font-semibold text-gray-800">Topic: <span className="font-normal text-gray-600">{topic}</span></p>
            <p className="text-sm font-semibold text-gray-800">Format: <span className="font-normal text-gray-600">{format}</span></p>
            {lesson && lesson !== "Unknown" && (
               <p className="text-sm font-semibold text-gray-800">Lesson: <span className="font-normal text-gray-600">{lesson}</span></p>
            )}
          </div>
          
          <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200 h-32 overflow-y-auto custom-scrollbar">
             <pre className="text-[10px] text-gray-500 font-mono whitespace-pre-wrap">{Quiz_Text}</pre>
          </div>
        </div>

        {success && (
          <div className="mt-6 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-center text-emerald-700 font-medium text-sm">
            ✅ Quiz shared successfully!
          </div>
        )}

      </div>
    </div>
  );
};

export default Email_Quiz;